import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { subscribeToPlans, PlanConfig, PLAN_DEFAULTS } from '../services/subscriptionService';
import {
  getLandingTexts,
  getLandingVideos,
  getLandingCards,
  getLandingArticles,
  subscribeToLandingTexts,
  LandingText,
  LandingVideo,
  LandingCard,
  LandingArticle,
  extractYouTubeId,
  extractVimeoId,
  detectVideoType
} from '../services/landingContentService';
import './LandingModern.css';

export const Landing: React.FC = () => {
  const [plans, setPlans] = React.useState<Record<string, PlanConfig>>(PLAN_DEFAULTS);
  const [landingTexts, setLandingTexts] = React.useState<LandingText | null>(null);
  const [videos, setVideos] = React.useState<LandingVideo[]>([]);
  const [cards, setCards] = React.useState<LandingCard[]>([]);
  const [articles, setArticles] = React.useState<LandingArticle[]>([]);

  React.useEffect(() => {
    // Subscribe aos planos em tempo real
    const unsubscribePlans = subscribeToPlans(setPlans);
    
    // Carregar textos
    getLandingTexts().then(setLandingTexts);
    const unsubscribeTexts = subscribeToLandingTexts(setLandingTexts);
    
    // Carregar vídeos, cards e artigos
    getLandingVideos().then(setVideos);
    getLandingCards().then(setCards);
    getLandingArticles().then(setArticles);
    
    // Cleanup: unsubscribe quando o componente desmontar
    return () => {
      unsubscribePlans();
      unsubscribeTexts();
    };
  }, []);

  return (
    <div className="ns-landing">
      {/* Header com Glass Morphism */}
      <header className="ns-landing-header-modern">
        <div className="ns-landing-header-content">
          <div className="ns-landing-logo-modern">
            <div className="ns-logo-icon-modern">
              <span>NS</span>
            </div>
            <span className="ns-logo-text-modern">NutriSync</span>
          </div>
          <nav className="ns-landing-nav-modern">
            <a href="#funcionalidades">Funcionalidades</a>
            <a href="#como-funciona">Como Funciona</a>
            <a href="#vantagens">Vantagens</a>
            <a href="#preco">Preços</a>
          </nav>
          <div className="ns-landing-cta-modern">
            <Link to="/login">
              <button className="ns-btn-login-modern">Entrar</button>
            </Link>
            <Link to="/signup">
              <button className="ns-btn-signup-modern">
                <span>Começar Grátis</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </Link>
          </div>
        </div>
      </header>

      <main className="ns-landing-main-modern">
        {/* Hero Section Ultra Moderna */}
        <section className="ns-hero-modern">
          <div className="ns-hero-background">
            <div className="ns-hero-gradient-1"></div>
            <div className="ns-hero-gradient-2"></div>
            <div className="ns-hero-gradient-3"></div>
            <div className="ns-hero-mesh"></div>
          </div>

          <div className="ns-hero-content-modern">
            <div className="ns-hero-content-left">
              <div className="ns-hero-badge-modern">
                <span>🐝</span>
                <span>{landingTexts?.heroBadge || 'Plataforma completa com painel web + app mobile'}</span>
              </div>

              <h1 className="ns-hero-title-modern">
                {landingTexts?.heroTitle ? (
                  <span dangerouslySetInnerHTML={{ __html: landingTexts.heroTitle.replace(/\n/g, '<br />') }} />
                ) : (
                  <>
                    Transforme sua
                    <br />
                    <span className="ns-hero-title-gradient">prática nutricional</span>
                    <br />
                    em uma experiência digital
                  </>
                )}
              </h1>

              <p className="ns-hero-subtitle-modern">
                {landingTexts?.heroSubtitle || 'Gerencie pacientes, crie planos alimentares personalizados e acompanhe a evolução em tempo real. <strong>Painel web profissional</strong> para você + <strong>app mobile intuitivo</strong> para seus pacientes.'}
              </p>

              <div className="ns-hero-actions-modern">
                <Link to="/signup">
                  <button className="ns-btn-primary-modern">
                    <span>Começar teste grátis por 7 dias</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7 14L12 10L7 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </Link>
                <a href="#como-funciona" className="ns-btn-secondary-modern">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 9L10 11L12 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Ver demonstração</span>
                </a>
              </div>

              <div className="ns-hero-stats-modern">
                <div className="ns-stat-modern">
                  <div className="ns-stat-icon-modern">⚡</div>
                  <div className="ns-stat-content-modern">
                    <div className="ns-stat-value-modern">7 dias</div>
                    <div className="ns-stat-label-modern">Teste grátis</div>
                  </div>
                </div>
                <div className="ns-stat-separator"></div>
                <div className="ns-stat-modern">
                  <div className="ns-stat-icon-modern">✓</div>
                  <div className="ns-stat-content-modern">
                    <div className="ns-stat-value-modern">100%</div>
                    <div className="ns-stat-label-modern">Funcionalidades</div>
                  </div>
                </div>
                <div className="ns-stat-separator"></div>
                <div className="ns-stat-modern">
                  <div className="ns-stat-icon-modern">🛡️</div>
                  <div className="ns-stat-content-modern">
                    <div className="ns-stat-value-modern">24/7</div>
                    <div className="ns-stat-label-modern">Suporte</div>
                  </div>
                </div>
              </div>

              <div className="ns-hero-trust-modern">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1L10 6L15 6L11 10L13 15L8 11L3 15L5 10L1 6L6 6L8 1Z" fill="currentColor" />
                </svg>
                <span>Sem cartão de crédito • Cancele quando quiser • Seguro e confiável</span>
              </div>
            </div>

            {/* Visual Cards */}
            <div className="ns-hero-visual-modern">
            <div className="ns-hero-card-modern ns-hero-card-1">
              <div className="ns-hero-card-header">
                <div className="ns-hero-card-icon">💻</div>
                <div className="ns-hero-card-badge">Web</div>
              </div>
              <h3>Painel Profissional</h3>
              <p>Dashboard completo com métricas em tempo real e gestão integrada</p>
              <div className="ns-hero-card-features">
                <span>📊 Dashboard</span>
                <span>👥 Pacientes</span>
                <span>📅 Agenda</span>
                <span>📈 Relatórios</span>
              </div>
            </div>

            <div className="ns-hero-card-modern ns-hero-card-2">
              <div className="ns-hero-card-header">
                <div className="ns-hero-card-icon">📱</div>
                <div className="ns-hero-card-badge ns-badge-accent">Mobile</div>
              </div>
              <h3>App para Pacientes</h3>
              <p>Seus pacientes acompanham tudo direto no celular com interface intuitiva</p>
              <div className="ns-hero-card-features">
                <span>🍽️ Planos</span>
                <span>⏰ Lembretes</span>
                <span>💬 Chat</span>
                <span>📊 Progresso</span>
              </div>
            </div>
          </div>
          </div>
        </section>

        {/* Por Que Escolher NutriSync */}
        <section className="ns-why-choose-section">
          <div className="ns-why-choose-content">
            <div className="ns-why-choose-item">
              <div className="ns-why-icon ns-why-icon-blue">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L15 8L22 9L17 14L19 21L12 17L5 21L7 14L2 9L9 8L12 2Z" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.2" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Interface Intuitiva</h3>
              <p>Design pensado para facilitar seu dia a dia, sem complicações</p>
            </div>
            <div className="ns-why-choose-item">
              <div className="ns-why-icon ns-why-icon-green">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.2" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Setup em Minutos</h3>
              <p>Comece a usar em menos de 5 minutos, sem treinamento complexo</p>
            </div>
            <div className="ns-why-choose-item">
              <div className="ns-why-icon ns-why-icon-purple">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.2" />
                  <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Tudo Integrado</h3>
              <p>Web + Mobile sincronizados automaticamente em tempo real</p>
            </div>
            <div className="ns-why-choose-item">
              <div className="ns-why-icon ns-why-icon-orange">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C12 2 20 4 20 12C20 17 16 21 12 22C8 21 4 17 4 12C4 4 12 2 12 2Z" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.2" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>Dados Protegidos</h3>
              <p>Criptografia de ponta e total conformidade com a LGPD</p>
            </div>
          </div>
        </section>

        {/* Como Funciona - Passo a Passo */}
        <section id="como-funciona" className="ns-how-it-works-modern">
          <div className="ns-section-header-modern">
            <span className="ns-section-tag-modern">Processo Simples</span>
            <h2>Como funciona o NutriSync</h2>
            <p>Em poucos passos, você estará gerenciando sua prática de forma profissional</p>
          </div>

          <div className="ns-steps-modern">
            <div className="ns-step-modern">
              <div className="ns-step-number-modern">
                <span>01</span>
                <div className="ns-step-line"></div>
              </div>
              <div className="ns-step-content-modern">
                <div className="ns-step-icon-modern">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 16L15 19L20 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3>Cadastre-se Gratuitamente</h3>
                <p>Crie sua conta em menos de 2 minutos. Sem cartão de crédito, sem compromisso.</p>
              </div>
            </div>

            <div className="ns-step-modern">
              <div className="ns-step-number-modern">
                <span>02</span>
                <div className="ns-step-line"></div>
              </div>
              <div className="ns-step-content-modern">
                <div className="ns-step-icon-modern">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="4" y="4" width="24" height="24" rx="4" stroke="currentColor" strokeWidth="2" />
                    <path d="M4 12H28M12 4V12M20 4V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <h3>Configure Seu Painel</h3>
                <p>Personalize seu painel, adicione suas informações e comece a cadastrar pacientes.</p>
              </div>
            </div>

            <div className="ns-step-modern">
              <div className="ns-step-number-modern">
                <span>03</span>
                <div className="ns-step-line"></div>
              </div>
              <div className="ns-step-content-modern">
                <div className="ns-step-icon-modern">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="10" r="6" stroke="currentColor" strokeWidth="2" />
                    <path d="M4 28C4 22 9 18 16 18C23 18 28 22 28 28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <h3>Adicione Pacientes</h3>
                <p>Cadastre seus pacientes e envie o link do app mobile para eles baixarem.</p>
              </div>
            </div>

            <div className="ns-step-modern">
              <div className="ns-step-number-modern">
                <span>04</span>
              </div>
              <div className="ns-step-content-modern">
                <div className="ns-step-icon-modern">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M4 16L12 8L18 14L28 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="8" r="2" fill="currentColor" />
                    <circle cx="18" cy="14" r="2" fill="currentColor" />
                    <circle cx="28" cy="4" r="2" fill="currentColor" />
                  </svg>
                </div>
                <h3>Acompanhe a Evolução</h3>
                <p>Monitore o progresso em tempo real e ajuste os planos conforme necessário.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Funcionalidades Modernas */}
        <section id="funcionalidades" className="ns-features-modern">
          <div className="ns-section-header-modern">
            <span className="ns-section-tag-modern">Recursos Completos</span>
            <h2>Tudo que você precisa em um só lugar</h2>
            <p>Ferramentas profissionais para otimizar seu tempo e melhorar resultados</p>
          </div>

          <div className="ns-features-grid-modern">
            <div className="ns-feature-card-modern">
              <div className="ns-feature-icon-container-modern">
                <div className="ns-feature-icon-bg-modern ns-bg-blue"></div>
                <span className="ns-feature-emoji-modern">📊</span>
              </div>
              <h3>Dashboard Inteligente</h3>
              <p>Visão completa da sua prática com métricas, gráficos e alertas em tempo real</p>
            </div>

            <div className="ns-feature-card-modern">
              <div className="ns-feature-icon-container-modern">
                <div className="ns-feature-icon-bg-modern ns-bg-green"></div>
                <span className="ns-feature-emoji-modern">👥</span>
              </div>
              <h3>Gestão de Pacientes</h3>
              <p>Prontuário digital completo com histórico, medições e evolução detalhada</p>
            </div>

            <div className="ns-feature-card-modern">
              <div className="ns-feature-icon-container-modern">
                <div className="ns-feature-icon-bg-modern ns-bg-orange"></div>
                <span className="ns-feature-emoji-modern">🍽️</span>
              </div>
              <h3>Planos Alimentares</h3>
              <p>Crie planos semanais personalizados que sincronizam automaticamente com o app</p>
            </div>

            <div className="ns-feature-card-modern">
              <div className="ns-feature-icon-container-modern">
                <div className="ns-feature-icon-bg-modern ns-bg-purple"></div>
                <span className="ns-feature-emoji-modern">📅</span>
              </div>
              <h3>Agenda Inteligente</h3>
              <p>Gerencie consultas presenciais e online com lembretes automáticos</p>
            </div>

            <div className="ns-feature-card-modern">
              <div className="ns-feature-icon-container-modern">
                <div className="ns-feature-icon-bg-modern ns-bg-pink"></div>
                <span className="ns-feature-emoji-modern">💬</span>
              </div>
              <h3>Chat em Tempo Real</h3>
              <p>Comunicação direta com pacientes através de mensagens instantâneas</p>
            </div>

            <div className="ns-feature-card-modern">
              <div className="ns-feature-icon-container-modern">
                <div className="ns-feature-icon-bg-modern ns-bg-teal"></div>
                <span className="ns-feature-emoji-modern">📈</span>
              </div>
              <h3>Relatórios Detalhados</h3>
              <p>Exporte relatórios completos de evolução e adesão em PDF profissional</p>
            </div>
          </div>
        </section>

        {/* Vantagens Competitivas */}
        <section id="vantagens" className="ns-advantages-modern">
          <div className="ns-section-header-modern">
            <span className="ns-section-tag-modern">Por que escolher</span>
            <h2>Vantagens que fazem a diferença</h2>
            <p>Compare e veja por que o NutriSync é a escolha certa para sua prática</p>
          </div>

          <div className="ns-comparison-grid-modern">
            <div className="ns-comparison-card-traditional">
              <div className="ns-comparison-header">
                <div className="ns-comparison-icon">📋</div>
                <h3>Método Tradicional</h3>
              </div>
              <ul className="ns-comparison-list">
                <li className="negative">⛔ Planilhas e papéis desorganizados</li>
                <li className="negative">⛔ Comunicação limitada por WhatsApp</li>
                <li className="negative">⛔ Dificuldade em acompanhar evolução</li>
                <li className="negative">⛔ Perda de tempo com tarefas manuais</li>
                <li className="negative">⛔ Sem backup automático de dados</li>
                <li className="negative">⛔ Difícil escalar o atendimento</li>
              </ul>
            </div>

            <div className="ns-comparison-card-nutrisync">
              <div className="ns-comparison-badge">✨ Melhor Escolha</div>
              <div className="ns-comparison-header">
                <div className="ns-comparison-icon">🚀</div>
                <h3>Com NutriSync</h3>
              </div>
              <ul className="ns-comparison-list">
                <li className="positive">✅ Tudo centralizado em uma plataforma</li>
                <li className="positive">✅ Chat integrado e notificações automáticas</li>
                <li className="positive">✅ Gráficos e métricas em tempo real</li>
                <li className="positive">✅ Automação de processos repetitivos</li>
                <li className="positive">✅ Backup em nuvem seguro (PHP/MySQL)</li>
                <li className="positive">✅ Atenda mais pacientes com menos esforço</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Funcionalidades Detalhadas */}
        <section className="ns-detailed-features-modern">
          <div className="ns-section-header-modern">
            <span className="ns-section-tag-modern">Recursos Avançados</span>
            <h2>Tudo que você precisa para trabalhar melhor</h2>
            <p>Uma plataforma completa pensada especialmente para nutricionistas</p>
          </div>

          <div className="ns-detailed-features-grid">
            <div className="ns-detailed-feature-card">
              <div className="ns-feature-number">01</div>
              <div className="ns-feature-icon-large">💻</div>
              <h3>Painel Web Profissional</h3>
              <p>Acesse de qualquer computador com interface responsiva e intuitiva</p>
              <ul className="ns-feature-benefits">
                <li>Dashboard com métricas importantes</li>
                <li>Gestão completa de pacientes</li>
                <li>Criação de planos alimentares</li>
                <li>Agendamento de consultas</li>
                <li>Exportação de relatórios em PDF</li>
              </ul>
            </div>

            <div className="ns-detailed-feature-card">
              <div className="ns-feature-number">02</div>
              <div className="ns-feature-icon-large">📱</div>
              <h3>App Mobile para Pacientes</h3>
              <p>Seus pacientes têm tudo na palma da mão (Android e iOS em breve)</p>
              <ul className="ns-feature-benefits">
                <li>Visualização de planos alimentares</li>
                <li>Registro de progresso e medidas</li>
                <li>Chat direto com você</li>
                <li>Lembretes de refeições</li>
                <li>Galeria de fotos de evolução</li>
              </ul>
            </div>

            <div className="ns-detailed-feature-card">
              <div className="ns-feature-number">03</div>
              <div className="ns-feature-icon-large">🤖</div>
              <h3>Inteligência Artificial</h3>
              <p>IA para auxiliar na criação de planos alimentares personalizados</p>
              <ul className="ns-feature-benefits">
                <li>Sugestões inteligentes de refeições</li>
                <li>Cálculo automático de macros</li>
                <li>Geração de planos semanais</li>
                <li>Ajustes baseados em objetivos</li>
                <li>Você sempre tem controle final</li>
              </ul>
            </div>

            <div className="ns-detailed-feature-card">
              <div className="ns-feature-number">04</div>
              <div className="ns-feature-icon-large">📊</div>
              <h3>Acompanhamento em Tempo Real</h3>
              <p>Monitore a evolução dos seus pacientes instantaneamente</p>
              <ul className="ns-feature-benefits">
                <li>Gráficos de peso e medidas</li>
                <li>Taxa de adesão ao plano</li>
                <li>Evolução de bioimpedância</li>
                <li>Comparação de fotos</li>
                <li>Alertas de não conformidade</li>
              </ul>
            </div>

            <div className="ns-detailed-feature-card">
              <div className="ns-feature-number">05</div>
              <div className="ns-feature-icon-large">💬</div>
              <h3>Comunicação Integrada</h3>
              <p>Fale com seus pacientes sem sair da plataforma</p>
              <ul className="ns-feature-benefits">
                <li>Chat em tempo real</li>
                <li>Notificações push no mobile</li>
                <li>Compartilhamento de arquivos</li>
                <li>Histórico completo de conversas</li>
                <li>Status online/offline</li>
              </ul>
            </div>

            <div className="ns-detailed-feature-card">
              <div className="ns-feature-number">06</div>
              <div className="ns-feature-icon-large">🔒</div>
              <h3>Segurança e Privacidade</h3>
              <p>Dados protegidos com tecnologia de ponta</p>
              <ul className="ns-feature-benefits">
                <li>Criptografia end-to-end</li>
                <li>Conformidade total com LGPD</li>
                <li>Backup automático diário</li>
                <li>Autenticação segura</li>
                <li>Controle de acesso por usuário</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Pricing Section Ultra Moderna */}
        <section id="preco" className="ns-pricing-modern">
          <div className="ns-section-header-modern">
            <span className="ns-section-tag-modern">Planos e Preços</span>
            <h2>Comece gratuitamente hoje</h2>
            <p>Teste todas as funcionalidades por 7 dias sem compromisso</p>
          </div>

          <div className="ns-pricing-card-modern">
            <div className="ns-pricing-badge-modern">
              <span>🎁</span>
              <span>Oferta Especial</span>
            </div>

            <div className="ns-pricing-header-modern">
              <h3>Plano Completo</h3>
              <div className="ns-pricing-value-modern">
                <span className="ns-pricing-currency-modern">R$</span>
                <span className="ns-pricing-amount-modern">{plans.basic?.price?.toFixed(2).replace('.', ',').split(',')[0]}</span>
                <span className="ns-pricing-decimal-modern">,{plans.basic?.price?.toFixed(2).replace('.', ',').split(',')[1]}</span>
                <span className="ns-pricing-period-modern">/mês</span>
              </div>
              <p className="ns-pricing-description-modern">
                Acesso completo a todas as funcionalidades. Sem taxas ocultas.
              </p>
            </div>

            <div className="ns-pricing-features-modern">
              <div className="ns-pricing-feature-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" fill="#10b981" opacity="0.1" />
                  <path d="M6 10L9 13L14 7" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>7 dias de teste grátis</span>
              </div>
              <div className="ns-pricing-feature-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" fill="#10b981" opacity="0.1" />
                  <path d="M6 10L9 13L14 7" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Todas as funcionalidades incluídas</span>
              </div>
              <div className="ns-pricing-feature-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" fill="#10b981" opacity="0.1" />
                  <path d="M6 10L9 13L14 7" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>App mobile para pacientes</span>
              </div>
              <div className="ns-pricing-feature-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" fill="#10b981" opacity="0.1" />
                  <path d="M6 10L9 13L14 7" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Suporte prioritário 24/7</span>
              </div>
              <div className="ns-pricing-feature-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" fill="#10b981" opacity="0.1" />
                  <path d="M6 10L9 13L14 7" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Atualizações constantes gratuitas</span>
              </div>
              <div className="ns-pricing-feature-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" fill="#10b981" opacity="0.1" />
                  <path d="M6 10L9 13L14 7" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Cancele quando quiser</span>
              </div>
            </div>

            <Link to="/signup">
              <button className="ns-btn-pricing-modern">
                <span>Começar teste grátis agora</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7 14L12 10L7 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </Link>

            <div className="ns-pricing-guarantee-modern">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2L12.5 5.5L17 6.5L13.5 10L14 15L10 12.5L6 15L6.5 10L3 6.5L7.5 5.5L10 2Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.1" />
              </svg>
              <span>Garantia de satisfação ou seu dinheiro de volta</span>
            </div>
          </div>
        </section>

        {/* Vídeos */}
        {videos.length > 0 && (
          <section className="ns-videos-section" style={{ padding: '4rem 2rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="ns-section-header-modern" style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <span className="ns-section-tag-modern">Vídeos</span>
              <h2>Conteúdo em Vídeo</h2>
              <p>Assista aos nossos vídeos explicativos e tutoriais</p>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
              maxWidth: '1200px',
              margin: '2rem auto 0'
            }}>
              {videos.map((video) => {
                const videoType = detectVideoType(video.url);
                const youtubeId = extractYouTubeId(video.url);
                const vimeoId = extractVimeoId(video.url);
                
                return (
                  <div key={video.id} style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <h3 style={{ color: 'white', marginBottom: '0.5rem' }}>{video.title}</h3>
                    {video.description && (
                      <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                        {video.description}
                      </p>
                    )}
                    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px' }}>
                      {videoType === 'youtube' && youtubeId ? (
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${youtubeId}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ position: 'absolute', top: 0, left: 0 }}
                        />
                      ) : videoType === 'vimeo' && vimeoId ? (
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://player.vimeo.com/video/${vimeoId}`}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          style={{ position: 'absolute', top: 0, left: 0 }}
                        />
                      ) : (
                        <video
                          controls
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                          src={video.url}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Cards Adicionais */}
        {cards.length > 0 && (
          <section className="ns-cards-section" style={{ padding: '4rem 2rem', background: '#f8fafc' }}>
            <div className="ns-section-header-modern" style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <span className="ns-section-tag-modern">Recursos</span>
              <h2 style={{ color: '#0f172a' }}>Recursos Adicionais</h2>
              <p style={{ color: '#64748b' }}>Explore nossos recursos e funcionalidades</p>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              maxWidth: '1200px',
              margin: '2rem auto 0'
            }}>
              {cards.map((card) => (
                <div key={card.id} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.2s',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{card.icon}</div>
                  <h3 style={{ color: '#0f172a', marginBottom: '0.5rem' }}>{card.title}</h3>
                  <p style={{ color: '#64748b', lineHeight: 1.6 }}>{card.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Artigos Científicos */}
        {articles.length > 0 && (
          <section className="ns-articles-section" style={{ padding: '4rem 2rem', background: '#0f172a' }}>
            <div className="ns-section-header-modern" style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <span className="ns-section-tag-modern">Artigos</span>
              <h2 style={{ color: 'white' }}>Artigos Científicos</h2>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Acesse nossos artigos e estudos científicos</p>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
              maxWidth: '1200px',
              margin: '2rem auto 0'
            }}>
              {articles.map((article) => (
                <div key={article.id} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  padding: '2rem',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <h3 style={{ color: 'white', marginBottom: '0.5rem' }}>{article.title}</h3>
                  {article.author && (
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      Por {article.author}
                    </p>
                  )}
                  {article.date && (
                    <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem', marginBottom: '1rem' }}>
                      {new Date(article.date).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '1rem', lineHeight: 1.6 }}>
                    {article.content.substring(0, 150)}...
                  </p>
                  {article.pdfUrl && (
                    <a
                      href={article.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        padding: '0.5rem 1rem',
                        background: '#00D09C',
                        color: '#0f172a',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    >
                      Ler Artigo →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA Final */}
        <section className="ns-final-cta-modern">
          <div className="ns-final-cta-content">
            <h2>Pronto para transformar sua prática nutricional?</h2>
            <p>Junte-se a centenas de nutricionistas que já modernizaram seu atendimento</p>
            <div className="ns-final-cta-actions">
              <Link to="/signup">
                <button className="ns-btn-final-cta-modern">
                  <span>Começar teste grátis por 7 dias</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7 14L12 10L7 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </Link>
              <p className="ns-final-cta-note">Sem cartão de crédito • Cancele quando quiser</p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer Moderno */}
      <footer className="ns-footer-modern">
        <div className="ns-footer-content-modern">
          <div className="ns-footer-brand-modern">
            <div className="ns-landing-logo-modern">
              <div className="ns-logo-icon-modern">
                <span>NS</span>
              </div>
              <span className="ns-logo-text-modern">NutriSync</span>
            </div>
            <p>Transformando a nutrição através da tecnologia</p>
            <div className="ns-footer-social-modern">
              <a href="#" className="ns-social-link-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 0C4.477 0 0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.879V12.89h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.989C16.343 19.129 20 14.99 20 10c0-5.523-4.477-10-10-10z" />
                </svg>
              </a>
              <a href="#" className="ns-social-link-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 0C4.477 0 0 4.477 0 10c0 5.013 3.693 9.153 8.505 9.876V12.83H5.938V10h2.567V7.797c0-2.533 1.509-3.93 3.816-3.93 1.105 0 2.262.197 2.262.197v2.487h-1.275c-1.255 0-1.647.78-1.647 1.58V10h2.804l-.448 2.83h-2.356v7.046C16.307 19.153 20 15.013 20 10c0-5.523-4.477-10-10-10z" />
                </svg>
              </a>
              <a href="#" className="ns-social-link-modern">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.826 7.838c.004.098.006.197.006.297 0 3.026-2.304 6.515-6.515 6.515-1.293 0-2.498-.379-3.511-1.029.179.021.361.032.546.032 1.072 0 2.059-.366 2.843-.981-.1 2-.784.364-1.642.898-.367.231-.796.366-1.261.366-.291 0-.571-.039-.838-.112a3.257 3.257 0 002.934-2.26 3.257 3.257 0 01-1.47-.056 3.253 3.253 0 01-2.608-3.188v-.041c.465.259.997.415 1.564.433A3.253 3.253 0 019.038 3.84c1.191 1.463 2.963 2.426 4.965 2.527a3.253 3.253 0 015.543-2.965 6.522 6.522 0 002.065-.789 3.262 3.262 0 01-1.43 1.798A6.482 6.482 0 0020 3.926a6.613 6.613 0 01-1.672 1.682l-.002.23z" />
                </svg>
              </a>
            </div>
          </div>

          <div className="ns-footer-links-modern">
            <div className="ns-footer-column-modern">
              <h5>Produto</h5>
              <a href="#funcionalidades">Funcionalidades</a>
              <a href="#como-funciona">Como Funciona</a>
              <a href="#preco">Preços</a>
              <a href="#vantagens">Vantagens</a>
            </div>

            <div className="ns-footer-column-modern">
              <h5>Empresa</h5>
              <Link to="/faq">FAQ</Link>
              <a href="mailto:contato@nutrisync.com.br">Contato</a>
              <a href="#">Blog</a>
            </div>

            <div className="ns-footer-column-modern">
              <h5>Legal</h5>
              <Link to="/privacidade">Privacidade</Link>
              <Link to="/termos-de-uso">Termos de Uso</Link>
              <Link to="/lgpd">LGPD</Link>
            </div>
          </div>
        </div>

        <div className="ns-footer-bottom-modern">
          <p>© 2024 NutriSync. Todos os direitos reservados.</p>
          <p>Feito com ❤️ para nutricionistas no Brasil</p>
        </div>
      </footer>
    </div>
  );
};
